function getEl<T extends HTMLElement>(id: string): T | null { return document.getElementById(id) as T | null; }
function getInput(id: string): HTMLInputElement | null { return getEl<HTMLInputElement>(id); }
function getSelect(id: string): HTMLSelectElement | null { return getEl<HTMLSelectElement>(id); }
function getButton(id: string): HTMLButtonElement | null { return getEl<HTMLButtonElement>(id); }
const Tabulator = (window as any).Tabulator;
const Papa = (window as any).Papa;
const XLSX = (window as any).XLSX;

import { state } from '../state';
import { apiCall } from '../api';
import { showToast, showLoader, hideLoader, dualListGetValues, formatBoolean } from '../utils';
import { createVirtualTable, getMassColumns, filterMassColumns, getDisplayColumnName, dualListInit, getSelectedMassGroups, updateMassGroupsToggleText, autoSelectMassGroups } from './common';

let currentSearchPayload: any = null;
let currentPage = 1;
let totalPages = 1;
let currentColumns: any[] = [];

    // -------------------- МАССОВЫЙ РЕЖИМ --------------------
    const fileInput = getInput('mass-files')!;
    const dropzone = getEl<HTMLElement>('mass-dropzone')!;

    // Drag & Drop
    dropzone.addEventListener('dragenter', (e: DragEvent) => {
        e.preventDefault();
        dropzone.style.borderColor = '#0097D8';
        dropzone.style.background = '#e6f0f8';
    });
    dropzone.addEventListener('dragover', (e: DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    });
    dropzone.addEventListener('dragleave', (e: DragEvent) => {
        e.preventDefault();
        dropzone.style.borderColor = '#ccc';
        dropzone.style.background = '#fafafa';
    });
    dropzone.addEventListener('drop', async (e: DragEvent) => {
        e.preventDefault();
        dropzone.style.borderColor = '#ccc';
        dropzone.style.background = '#fafafa';
        console.log('drop event', e.dataTransfer?.files.length, e.dataTransfer?.files);
        const files = Array.from(e.dataTransfer?.files || []).filter((f: File) => {
            const n = f.name.toLowerCase();
            return n.endsWith('.csv') || n.endsWith('.txt') || n.endsWith('.xlsx') || n.endsWith('.xls') || n.endsWith('.xlsb') || n.endsWith('.ods') || n.endsWith('.docx');
        });
        if (!files.length) {
            showToast('Перетащите файлы .csv, .txt, .xlsx, .xls, .xlsb, .ods или .docx', 'warning');
            return;
        }
        await processFiles(files);
    });

    function isExcelExt(ext: string): boolean {
        return ['.xlsx', '.xls', '.xlsb', '.ods'].includes(ext);
    }

    function makeUnitName(fileName: string, sheetName?: string): string {
        return sheetName ? `${fileName} [${sheetName}]` : fileName;
    }

    function getUnitsForPhysical(pf: any): any[] {
        return state.loadedFiles.filter(u => u.sourceFile === pf);
    }

    function createUnit(pf: any, sheetName?: string): any {
        return {
            sourceFile: pf,
            file: pf.file,
            name: makeUnitName(pf.name, sheetName),
            sheetName: sheetName || undefined,
            ext: pf.ext,
            columns: pf.columns || [],
            titleColumns: [],
            titleDelimiter: ' ',
            headerRow: 0,
            specialColumns: {},
            excludeColumns: [],
            csvParams: { ...(pf.csvParams || {}) },
            preview: [],
            dataRows: pf.dataRows || [],
            specStandard: false,
            specColumns: pf.specColumns ? { ...pf.specColumns } : null,
            specDelimiters: pf.specDelimiters ? { ...pf.specDelimiters } : {}
        };
    }

    function syncLoadedFiles(): void {
        const oldUnits = state.loadedFiles;
        const preserved = new Map<string, any>();
        oldUnits.forEach(u => {
            if (!u.sourceFile) return;
            const pfIdx = state.files.indexOf(u.sourceFile);
            if (pfIdx < 0) return;
            const pf = state.files[pfIdx];
            if (isExcelExt(pf.ext)) {
                if (u.sheetName && (pf.selectedSheets || []).includes(u.sheetName)) {
                    preserved.set(`${pfIdx}|${u.sheetName}`, u);
                }
            } else {
                preserved.set(`${pfIdx}|`, u);
            }
        });

        const newLoaded: any[] = [];
        state.files.forEach((pf, pfIdx) => {
            if (isExcelExt(pf.ext)) {
                (pf.selectedSheets || []).forEach((sheetName: string) => {
                    const key = `${pfIdx}|${sheetName}`;
                    let unit = preserved.get(key);
                    if (unit) {
                        unit.sourceFile = pf;
                        unit.file = pf.file;
                        unit.name = makeUnitName(pf.name, sheetName);
                        unit.ext = pf.ext;
                        unit.sheetName = sheetName;
                    } else {
                        unit = createUnit(pf, sheetName);
                    }
                    newLoaded.push(unit);
                });
            } else {
                const key = `${pfIdx}|`;
                let unit = preserved.get(key);
                if (unit) {
                    unit.sourceFile = pf;
                    unit.file = pf.file;
                    unit.name = pf.name;
                    unit.ext = pf.ext;
                    unit.sheetName = undefined;
                } else {
                    unit = createUnit(pf);
                }
                newLoaded.push(unit);
            }
        });
        state.loadedFiles = newLoaded;

        // Propagate first unit columns back to the physical file for spec UI
        state.files.forEach(pf => {
            const units = getUnitsForPhysical(pf);
            if (units.length && units[0].columns) {
                pf.columns = units[0].columns;
            }
        });
    }

    async function processFiles(files: File[], append = false) {
        console.log('processFiles', files.length, files.map(f => f.name), 'append=', append);
        if (!append) {
            state.files = [];
            state.loadedFiles = [];
            clearMassResults();
        }
        const existingCount = state.files.length;
        for (const file of files) {
            try {
                const lowerName = file.name.toLowerCase();
                const ext = lowerName.slice(lowerName.lastIndexOf('.'));
                const physical: any = {
                    file,
                    name: file.name,
                    ext,
                    size: file.size,
                    sheets: [],
                    selectedSheets: [],
                    hasHidden: false,
                    showHiddenSheets: false,
                    specMode: 'off',
                    specColumns: null,
                    specDelimiters: {},
                    specStandard: false,
                    docxStandard: false,
                    specForcedSheets: []
                };
                if (isExcelExt(ext) || ext === '.docx') {
                    state.files.push(physical);
                    continue;
                }
                const data = await readFileData(file);
                console.log('readFileData ok', file.name, data.rows.length);
                physical.dataRows = data.rows || [];
                physical.columns = data.columns || [];
                physical.csvParams = data.csvParams || {};
                state.files.push(physical);
            } catch (err) {
                console.error('readFileData error', file.name, err);
                showToast('Ошибка чтения ' + file.name + ': ' + (err as Error).message, 'error');
            }
        }
        syncLoadedFiles();
        renderFileList();
        // Load Excel sheet lists for newly added files, then detect all new files in one batch
        const newPhysicalFiles: any[] = [];
        for (let i = existingCount; i < state.files.length; i++) {
            const pf = state.files[i];
            newPhysicalFiles.push(pf);
            if (isExcelExt(pf.ext)) {
                await loadExcelSheets(pf, i);
            }
        }
        if (newPhysicalFiles.length) {
            await autoDetectColumnsBatch(newPhysicalFiles);
        }
    }

    async function loadExcelSheets(physicalFile: any, idx: number) {
        const formData = new FormData();
        formData.append('file', physicalFile.file);
        const res = await apiCall('/api/excel_sheets', 'POST', formData, true);
        if (!res) return;
        physicalFile.sheets = res.sheets || [];
        physicalFile.hasHidden = res.has_hidden || false;
        // По умолчанию выбираем первый видимый лист
        const firstVisible = physicalFile.sheets.find((s: any) => s.visible);
        physicalFile.selectedSheets = firstVisible ? [firstVisible.name] : (physicalFile.sheets[0] ? [physicalFile.sheets[0].name] : []);
        syncLoadedFiles();
        renderFileList();
    }

    function renderFileList() {
        const filelist = getEl<HTMLElement>('mass-filelist');
        if (!filelist) return;
        if (!state.files.length) {
            filelist.innerHTML = '<div class="warning">Файлы не загружены</div>';
            return;
        }
        let html = '';
        state.files.forEach((f, idx) => {
            html += `<div class="file-item" style="border:1px solid #e0e0e0; border-radius:8px; padding:12px; margin-bottom:10px; background:#fff;">`;
            html += `<div style="font-weight:600; margin-bottom:8px;">📄 ${f.name}</div>`;

            // Excel: компактный выбор листов
            if (isExcelExt(f.ext) && f.sheets?.length) {
                const selectedCount = f.selectedSheets.length;
                const totalCount = f.sheets.length;
                const panelOpen = f.sheetPanelOpen ?? (f.sheets.length <= 8);
                html += `<div class="sheet-bar" style="margin-bottom:8px;">`;
                html += `<span>📑 Листы: <strong>${selectedCount}</strong> из ${totalCount} выбрано</span>`;
                html += `<button type="button" data-action="toggleSheetPanel" data-file-idx="${idx}" class="secondary" style="font-size:12px; padding:2px 10px;">${panelOpen ? 'Скрыть ▲' : 'Изменить ▼'}</button>`;
                html += `</div>`;
                if (panelOpen) {
                    html += `<div class="sheet-panel">`;
                    html += `<div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:8px;">`;
                    html += `<input type="text" class="sheet-filter" placeholder="Найти лист..." data-action="filterSheets" data-file-idx="${idx}">`;
                    html += `<button type="button" data-action="onSelectAllSheets" data-file-idx="${idx}" style="font-size:11px; padding:2px 8px;">Выделить все / снять</button>`;
                    if (f.hasHidden) {
                        const hiddenChecked = f.showHiddenSheets ? 'checked' : '';
                        html += `<label style="display:flex; align-items:center; gap:4px; font-size:12px; color:#666; cursor:pointer;">`;
                        html += `<input type="checkbox" data-file-idx="${idx}" ${hiddenChecked} data-action="onShowHiddenToggle"> Показывать скрытые листы</label>`;
                    }
                    html += `</div>`;
                    html += `<div class="sheet-list" id="sheet_list_${idx}">`;
                    f.sheets.forEach((sheet: any) => {
                        const isHidden = !sheet.visible;
                        if (isHidden && !f.showHiddenSheets) return;
                        const checked = f.selectedSheets.includes(sheet.name) ? 'checked' : '';
                        const hiddenClass = isHidden ? 'hidden-sheet' : '';
                        const hiddenLabel = isHidden ? ' <span style="color:#888; font-size:11px;">(скрыт)</span>' : '';
                        const safeTitle = sheet.name.replace(/"/g, '&quot;');
                        const specForced = (f.specForcedSheets || []).includes(sheet.name);
                        const specForcedChecked = specForced ? 'checked' : '';
                        html += `<div class="${hiddenClass}" style="display:flex; align-items:center; gap:8px; margin-bottom:4px;">`;
                        html += `<label title="${safeTitle}" style="display:flex; align-items:center; gap:4px; flex:1;">`;
                        html += `<input type="checkbox" data-file-idx="${idx}" data-sheet="${sheet.name}" ${checked} data-action="onSheetToggle">`;
                        html += `<span>${sheet.name}${hiddenLabel}</span></label>`;
                        html += `<label style="display:flex; align-items:center; gap:4px; font-size:11px; color:#666; white-space:nowrap;">`;
                        html += `<input type="checkbox" data-file-idx="${idx}" data-sheet="${sheet.name}" ${specForcedChecked} data-action="onSheetSpecToggle">`;
                        html += `<span>Спек</span></label>`;
                        html += `</div>`;
                    });
                    html += `</div></div>`;
                }
            }

            // Чекбокс Спецификация
            const specAuto = f.specMode === 'auto';
            const specForced = f.specMode === 'forced';
            const specDisabled = specAuto ? 'disabled' : '';
            const specChecked = (specAuto || specForced) ? 'checked' : '';
            const specLabel = specAuto ? '📝 Спецификация (авто)' : '📝 Спецификация';
            html += `<div style="margin-bottom:8px;">`;
            html += `<label style="display:flex; align-items:center; gap:6px; font-size:13px; cursor:pointer;">`;
            html += `<input type="checkbox" id="spec_${idx}" ${specChecked} ${specDisabled} data-action="onSpecToggle" data-idx="${idx}">`;
            html += `<span>${specLabel}</span>`;
            html += `</label>`;
            if (specForced) {
                html += `<button type="button" data-action="toggleSpecPanel" data-idx="${idx}" style="margin-left:24px; font-size:12px; padding:2px 8px;">⚙️ Настроить колонки</button>`;
                html += `<div id="spec_panel_${idx}" style="display:none; margin-left:24px; margin-top:8px; padding:10px; background:#f8f9fa; border-radius:6px; font-size:12px;">`;
                html += renderSpecPanel(f, idx);
                html += `</div>`;
            }
            html += `</div>`;

            // Статус
            const units = getUnitsForPhysical(f);
            const unitRows = units.reduce((sum: number, u: any) => sum + (u.dataRows?.length || 0), 0);
            if (isExcelExt(f.ext) && !f.sheets?.length) {
                html += `<div style="font-size:12px; color:#888;">Требуется автоопределение...</div>`;
            } else if (f.specMode !== 'off' && f.specStandard) {
                html += `<div style="font-size:12px; color:green;">✅ Спецификация обнаружена. Колонки определены автоматически.</div>`;
            } else if (unitRows > 0) {
                html += `<div style="font-size:12px; color:#555;">${unitRows} строк</div>`;
            }
            html += `</div>`;
        });
        const totalRows = state.loadedFiles.reduce((sum, f) => sum + (f.dataRows?.length || 0), 0);
        if (totalRows > 0) {
            html += `<div style="margin-top:4px; font-size:13px; color:#555;">Всего строк в файлах: ${totalRows}</div>`;
        }
        filelist.innerHTML = html;
    }

    function renderSpecPanel(f: any, idx: number): string {
        const cols = f.columns || [];
        const buildSelect = (id, selected, multiple) => {
            const multiAttr = multiple ? 'multiple size="3"' : '';
            let opts = multiple ? '' : '<option value="">-- нет --</option>';
            cols.forEach(c => {
                const sel = Array.isArray(selected) ? selected.includes(c) : selected === c;
                opts += `<option value="${c}" ${sel ? 'selected' : ''}>${colOptionLabel(f, c)}</option>`;
            });
            return `<select id="${id}" ${multiAttr} style="width:180px; font-size:12px;">${opts}</select>`;
        };
        const mapping = f.specColumns || {};
        const delims = f.specDelimiters || {};
        let html = '<div style="display:grid; grid-template-columns: auto 1fr auto; gap:6px 10px; align-items:center;">';
        const fields = [
            {key:'item', label:'Позиция', multi:false},
            {key:'title_full', label:'Наименование', multi:true},
            {key:'doc', label:'Тип/марка/док', multi:true},
            {key:'code', label:'Код продукции', multi:false},
            {key:'supplier', label:'Поставщик', multi:true},
            {key:'unit', label:'Ед. изм.', multi:false},
            {key:'qty', label:'Количество', multi:false},
            {key:'weight', label:'Масса', multi:false},
            {key:'note', label:'Примечание', multi:true},
        ];
        fields.forEach(fld => {
            const sel = mapping[fld.key] || (fld.multi ? [] : '');
            const delim = delims[fld.key] || ' ';
            html += `<div>${fld.label}</div>`;
            html += `<div>${buildSelect(`spec_${idx}_${fld.key}`, sel, fld.multi)}</div>`;
            if (fld.multi) {
                html += `<div>разд: <input type="text" id="spec_${idx}_delim_${fld.key}" value="${delim}" style="width:40px; font-size:12px;"></div>`;
            } else {
                html += `<div></div>`;
            }
        });
        html += '</div>';
        html += `<div style="margin-top:8px; display:flex; gap:8px;">`;
        html += `<button type="button" data-action="applySpecColumns" data-idx="${idx}" style="font-size:12px; padding:2px 10px;">Применить</button>`;
        html += `<button type="button" data-action="autoSpecColumns" data-idx="${idx}" style="font-size:12px; padding:2px 10px;">Автоподбор</button>`;
        html += `</div>`;
        return html;
    }

    (window as any).onSheetToggle = async function (el: HTMLInputElement) {
        const idx = parseInt(el.dataset.fileIdx || '0');
        const sheet = el.dataset.sheet || '';
        const pf = state.files[idx];
        if (!pf) return;
        if (el.checked) {
            if (!pf.selectedSheets.includes(sheet)) pf.selectedSheets.push(sheet);
        } else {
            pf.selectedSheets = pf.selectedSheets.filter((s: string) => s !== sheet);
        }
        syncLoadedFiles();
        await autoDetectColumnsBatch([pf]);
        renderFileList();
    };

    (window as any).onSheetSpecToggle = async function (el: HTMLInputElement) {
        const idx = parseInt(el.dataset.fileIdx || '0');
        const sheet = el.dataset.sheet || '';
        const pf = state.files[idx];
        if (!pf) return;
        pf.specForcedSheets = pf.specForcedSheets || [];
        if (el.checked) {
            if (!pf.specForcedSheets.includes(sheet)) pf.specForcedSheets.push(sheet);
        } else {
            pf.specForcedSheets = pf.specForcedSheets.filter((s: string) => s !== sheet);
        }
        await autoDetectColumnsBatch([pf]);
        renderFileList();
    };

    (window as any).onShowHiddenToggle = function (el: HTMLInputElement) {
        const idx = parseInt(el.dataset.fileIdx || '0');
        const pf = state.files[idx];
        if (!pf) return;
        pf.showHiddenSheets = el.checked;
        renderFileList();
    };

    (window as any).onSelectAllSheets = async function (idx: number) {
        const pf = state.files[idx];
        if (!pf) return;
        const visibleSheets = pf.sheets
            .filter((s: any) => s.visible || pf.showHiddenSheets)
            .map((s: any) => s.name);
        const allSelected = visibleSheets.every((s: string) => pf.selectedSheets.includes(s));
        if (allSelected) {
            pf.selectedSheets = [];
        } else {
            pf.selectedSheets = [...new Set([...pf.selectedSheets, ...visibleSheets])];
        }
        syncLoadedFiles();
        await autoDetectColumnsBatch([pf]);
        renderFileList();
    };

    (window as any).onSpecToggle = async function (el: HTMLInputElement, idx: number) {
        const pf = state.files[idx];
        if (!pf) return;
        if (el.checked) {
            pf.specMode = 'forced';
        } else {
            pf.specMode = 'off';
            pf.specColumns = null;
            pf.specDelimiters = {};
        }
        renderFileList();
        if (pf.specMode === 'forced') {
            await autoDetectColumnsBatch([pf]);
        }
    };

    (window as any).toggleSpecPanel = function (idx: number) {
        const panel = getEl<HTMLElement>('spec_panel_' + idx);
        if (panel) panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
    };

    (window as any).toggleSheetPanel = function (idx: number) {
        const pf = state.files[idx];
        if (!pf) return;
        pf.sheetPanelOpen = !pf.sheetPanelOpen;
        renderFileList();
    };

    (window as any).filterSheets = function (el: HTMLInputElement) {
        const idx = parseInt(el.dataset.fileIdx || '0');
        const query = el.value.toLowerCase();
        const list = getEl<HTMLElement>('sheet_list_' + idx);
        if (!list) return;
        list.querySelectorAll('label').forEach((label: HTMLLabelElement) => {
            const text = (label.textContent || '').toLowerCase();
            label.style.display = text.includes(query) ? 'flex' : 'none';
        });
    };

    (window as any).applySpecColumns = async function (idx: number) {
        const pf = state.files[idx];
        if (!pf) return;
        const fields = ['item','title_full','doc','code','supplier','unit','qty','weight','note'];
        const mapping: Record<string, any> = {};
        const delimiters: Record<string, any> = {};
        fields.forEach(key => {
            const sel = getSelect(`spec_${idx}_${key}`);
            if (!sel) return;
            if (sel.multiple) {
                const vals = Array.from(sel.selectedOptions).map(o => o.value).filter(v => v);
                if (vals.length) mapping[key] = vals;
                const delimInput = getInput(`spec_${idx}_delim_${key}`);
                if (delimInput) delimiters[key] = delimInput.value || ' ';
            } else {
                const val = sel.value;
                if (val) mapping[key] = [val];
            }
        });
        pf.specColumns = mapping;
        pf.specDelimiters = delimiters;
        const units = getUnitsForPhysical(pf);
        units.forEach((unit: any) => {
            unit.specColumns = { ...mapping };
            unit.specDelimiters = { ...delimiters };
        });
        await autoDetectColumnsBatch([pf]);
        showToast('Колонки спецификации применены', 'success');
    };

    (window as any).autoSpecColumns = function (idx: number) {
        const pf = state.files[idx];
        if (!pf) return;
        const cols = pf.columns || [];
        const mapping: Record<string, any> = {};
        const hints = [
            {key:'item', sub:['поз']},
            {key:'title_full', sub:['наименован', 'техническ']},
            {key:'doc', sub:['тип', 'марка', 'документ']},
            {key:'code', sub:['код']},
            {key:'supplier', sub:['поставщик']},
            {key:'unit', sub:['ед', 'измер']},
            {key:'qty', sub:['кол']},
            {key:'weight', sub:['масса']},
            {key:'note', sub:['примечан']},
        ];
        hints.forEach(h => {
            const found = cols.filter(c => h.sub.some(s => c.toLowerCase().includes(s)));
            if (found.length) mapping[h.key] = found;
        });
        pf.specColumns = mapping;
        pf.specDelimiters = {};
        renderFileList();
        (window as any).toggleSpecPanel(idx);
        showToast('Автоподбор выполнен', 'success');
    };

    fileInput.addEventListener('change', async (e: Event) => {
        const files = Array.from((e.target as HTMLInputElement).files || []);
        console.log('fileInput change', files.length);
        if (!files.length) return;
        await processFiles(files);
        fileInput.value = '';
    });

    function readFileData(file: File): Promise<any> {
        return new Promise((resolve, reject) => {
            const lowerName = file.name.toLowerCase();
            // CSV / TXT — читаем через PapaParse
            if (lowerName.endsWith('.csv') || lowerName.endsWith('.txt')) {
                Papa.parse(file, {
                    complete: function (results: any) {
                        try {
                            const rows = results.data;
                            if (!rows || rows.length === 0) {
                                resolve({ rows: [], columns: [], csvParams: { delimiter: results.meta.delimiter } });
                                return;
                            }
                            const cols = rows[0].map((c, i) => c || `col_${i}`);
                            const dataRows = rows.slice(1).filter(r => r.some(c => c !== '' && c != null)).map(r => {
                                let obj: Record<string, any> = {};
                                cols.forEach((c, i) => obj[c] = r[i] !== undefined ? r[i] : '');
                                return obj;
                            });
                            resolve({ rows: dataRows, columns: cols, csvParams: { delimiter: results.meta.delimiter } });
                        } catch (err) {
                            reject(err);
                        }
                    },
                    error: function (err: any) {
                        reject(new Error('PapaParse error: ' + err.message));
                    },
                    header: false,
                    skipEmptyLines: true,
                    delimiter: '', // автоопределение разделителя
                });
                return;
            }
            // XLS / XLSX — читаем через SheetJS
            const reader = new FileReader();
            reader.onload = function (e: ProgressEvent<FileReader>) {
                try {
                    if (typeof XLSX === 'undefined') {
                        reject(new Error('Библиотека XLSX не загружена. Обновите страницу.'));
                        return;
                    }
                    const data = new Uint8Array((e.target as FileReader).result as ArrayBuffer);
                    const workbook = XLSX.read(data, { type: 'array', cellFormula: false, cellHTML: false });
                    const sheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(sheet, { raw: true, defval: '' });
                    const cols = Object.keys(jsonData[0] || {});
                    resolve({ rows: jsonData, columns: cols, csvParams: {} });
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = () => reject(new Error('FileReader error'));
            reader.readAsArrayBuffer(file);
        });
    }

    async function autoDetectColumns(unit: any, idx: number): Promise<void> {
        const formData = new FormData();
        formData.append('file', unit.file);
        // Для CSV/TXT передаём текущие настройки кодировки/разделителя, если есть
        if (unit.csvParams) {
            if (unit.csvParams.encoding) formData.append('encoding', unit.csvParams.encoding);
            if (unit.csvParams.delimiter) formData.append('delimiter', unit.csvParams.delimiter);
        }
        // Для Excel передаём лист текущей единицы
        if (isExcelExt(unit.ext) && unit.sheetName) {
            formData.append('sheet_names', JSON.stringify([unit.sheetName]));
        }
        const pf = unit.sourceFile;
        if (pf && pf.specMode === 'forced') {
            formData.append('force_spec', 'true');
            if (pf.specColumns) {
                formData.append('spec_columns', JSON.stringify(pf.specColumns));
                formData.append('spec_delimiters', JSON.stringify(pf.specDelimiters || {}));
            }
        }
        const res = await apiCall('/api/detect_columns', 'POST', formData, true);
        if (!res) return;
        const sc = unit.specialColumns || {};
        if (res.title_column) sc.title = res.title_column;
        if (res.qty_column) sc.qty = res.qty_column;
        if (res.unit_column) sc.qty_unit = res.unit_column;
        if (res.request_no_column) sc.request_no = res.request_no_column;
        if (res.req_pos_column) sc.req_pos = res.req_pos_column;
        unit.specialColumns = sc;
        unit.headerRow = res.header_row || 0;
        unit.preview = res.preview || [];
        if (res.all_columns) {
            unit.columns = res.all_columns;
        }

        // CSV/TXT — сохраняем автоопределённые кодировку и разделитель
        if (res.detected_encoding !== undefined) {
            unit.csvParams = unit.csvParams || {};
            unit.csvParams.encoding = res.detected_encoding;
            unit.csvParams.delimiter = res.detected_delimiter;
        }

        // Спецификация (DOCX / XLSX)
        if (res.spec_standard !== undefined) {
            unit.specStandard = res.spec_standard;
            if (pf) {
                pf.specStandard = res.spec_standard;
                if (res.spec_standard && pf.specMode !== 'forced') {
                    pf.specMode = 'auto';
                }
                if (res.spec_columns) {
                    pf.specColumns = res.spec_columns;
                }
            }
            if (res.spec_standard) {
                unit.titleColumns = ['title_full'];
                if (res.spec_columns) {
                    unit.specColumns = res.spec_columns;
                }
            }
        }

        // DOCX — обратная совместимость
        if (res.docx_standard !== undefined) {
            unit.docxStandard = res.docx_standard;
            if (pf) {
                pf.docxStandard = res.docx_standard;
                if (res.docx_standard && pf.specMode !== 'forced') {
                    pf.specMode = 'auto';
                }
            }
            if (res.docx_standard && !unit.specStandard) {
                unit.specStandard = true;
                unit.titleColumns = ['title_full'];
            }
        }

        renderFileList();
        renderMassPreview(idx);

        // Try to restore previously saved column configuration for this header set
        await applyColumnPresetIfAny(unit);

        showToast(`Колонки для ${unit.name} определены автоматически`, 'success');
    }

    async function autoDetectColumnsBatch(physicalFiles: any[]): Promise<void> {
        if (!physicalFiles.length) return;
        const configs = physicalFiles.map((pf: any) => {
            const cfg: any = {
                sheet_names: isExcelExt(pf.ext) ? (pf.selectedSheets || []) : [],
            };
            if (pf.specMode === 'forced') {
                cfg.force_spec = true;
                cfg.spec_columns = pf.specColumns || null;
                cfg.spec_delimiters = pf.specDelimiters || null;
            } else if (isExcelExt(pf.ext) && (pf.specForcedSheets || []).length) {
                cfg.force_spec_sheets = pf.specForcedSheets;
            }
            if (!isExcelExt(pf.ext) && (pf.ext === '.csv' || pf.ext === '.txt')) {
                cfg.encoding = pf.csvParams?.encoding;
                cfg.delimiter = pf.csvParams?.delimiter;
            }
            return cfg;
        });

        const formData = new FormData();
        physicalFiles.forEach((pf: any) => formData.append('files', pf.file));
        formData.append('configs', JSON.stringify(configs));
        formData.append('match_presets', 'true');
        formData.append('source_type', 'req');

        const res = await apiCall('/api/detect_columns_batch', 'POST', formData, true);
        if (!res || !res.files) return;

        const updatedUnitIndices = new Set<number>();
        for (const fileResult of res.files) {
            const pf = physicalFiles[fileResult.file_index];
            if (!pf) continue;
            const units = getUnitsForPhysical(pf);
            let anySpecStandard = false;
            let anyDocxStandard = false;
            for (const unit of units) {
                const sheetKey = unit.sheetName || '__default__';
                const r = fileResult.results[sheetKey];
                if (!r) continue;
                const sc = unit.specialColumns || {};
                if (r.title_column) sc.title = r.title_column;
                if (r.qty_column) sc.qty = r.qty_column;
                if (r.unit_column) sc.qty_unit = r.unit_column;
                if (r.request_no_column) sc.request_no = r.request_no_column;
                if (r.req_pos_column) sc.req_pos = r.req_pos_column;
                unit.specialColumns = sc;
                unit.headerRow = r.header_row || 0;
                unit.preview = r.preview || [];
                if (r.all_columns) {
                    unit.columns = r.all_columns;
                }

                if (r.detected_encoding !== undefined) {
                    unit.csvParams = unit.csvParams || {};
                    unit.csvParams.encoding = r.detected_encoding;
                    unit.csvParams.delimiter = r.detected_delimiter;
                }

                if (r.spec_standard !== undefined) {
                    unit.specStandard = r.spec_standard;
                    if (r.spec_standard) {
                        anySpecStandard = true;
                        unit.titleColumns = ['title_full'];
                        if (r.spec_columns) {
                            unit.specColumns = r.spec_columns;
                            unit.specDelimiters = r.spec_delimiters || {};
                        }
                    }
                }

                if (r.docx_standard !== undefined) {
                    unit.docxStandard = r.docx_standard;
                    if (r.docx_standard && !unit.specStandard) {
                        anyDocxStandard = true;
                        unit.specStandard = true;
                        unit.titleColumns = ['title_full'];
                    }
                }

                if (r.exact_match && r.preset) {
                    applyColumnConfig(unit, r.preset);
                    unit.suggestedColumns = new Set<string>();
                } else if (r.column_suggestions && Object.keys(r.column_suggestions).length) {
                    applyColumnSuggestions(unit, r.column_suggestions);
                }

                const uidx = state.loadedFiles.indexOf(unit);
                if (uidx >= 0) updatedUnitIndices.add(uidx);
            }
            if (anySpecStandard && pf.specMode !== 'forced') {
                pf.specStandard = true;
                pf.specMode = 'auto';
            }
            if (anyDocxStandard && pf.specMode !== 'forced') {
                pf.docxStandard = true;
                pf.specMode = 'auto';
            }
        }

        // Propagate columns back to physical files
        state.files.forEach((pf: any) => {
            const units = getUnitsForPhysical(pf);
            if (units.length && units[0].columns) {
                pf.columns = units[0].columns;
            }
        });

        renderFileList();
        updatedUnitIndices.forEach(idx => renderMassPreview(idx));
        if (updatedUnitIndices.size) {
            showToast('Колонки определены автоматически', 'success');
        }
    }

    async function applyColumnPresetIfAny(unit: any): Promise<void> {
        const headers = unit.columns || [];
        if (!headers.length) return;
        try {
            const res = await apiCall('/api/column_presets/match', 'POST', {
                source_type: 'req',
                headers,
            });
            if (!res) return;
            if (res.exact_match && res.preset) {
                applyColumnConfig(unit, res.preset);
                unit.suggestedColumns = new Set<string>();
                showToast(`Применён сохранённый пресет колонок для ${unit.name}`, 'success');
            } else if (res.column_suggestions && Object.keys(res.column_suggestions).length) {
                applyColumnSuggestions(unit, res.column_suggestions);
                showToast(`Подставлены сохранённые колонки для ${unit.name}`, 'info');
            }
        } catch (e) {
            console.error('Column preset match failed:', e);
        }
    }

    function buildColumnConfig(unit: any): any {
        const sc = unit.specialColumns || {};
        const mapping: Record<string, string> = {};
        (unit.titleColumns || []).forEach((c: string) => mapping[c] = 'title');
        if (sc.id_column) mapping[sc.id_column] = 'id_column';
        if (sc.qty) mapping[sc.qty] = 'qty';
        if (sc.qty_unit) mapping[sc.qty_unit] = 'qty_unit';
        if (sc.request_no) mapping[sc.request_no] = 'request_no';
        if (sc.req_pos) mapping[sc.req_pos] = 'request_pos';
        if (sc.territory_column) mapping[sc.territory_column] = 'territory_column';
        (unit.excludeColumns || []).forEach((c: string) => mapping[c] = 'exclude');
        return {
            title_columns: unit.titleColumns || [],
            title_delimiter: unit.titleDelimiter || ' ',
            id_column: sc.id_column || '',
            qty_column: sc.qty || '',
            qty_unit_column: sc.qty_unit || '',
            request_no_column: sc.request_no || '',
            request_pos_column: sc.req_pos || '',
            territory_column: sc.territory_column || '',
            territory_value: sc.territory_value || '',
            exclude_columns: unit.excludeColumns || [],
            csv_params: unit.csvParams || {},
            mapping,
        };
    }

    function applyColumnConfig(unit: any, config: any): void {
        if (!config) return;
        if (config.title_columns && config.title_columns.length) {
            unit.titleColumns = [...config.title_columns];
        }
        if (config.title_delimiter !== undefined) {
            unit.titleDelimiter = config.title_delimiter;
        }
        const sc = unit.specialColumns || {};
        if (config.id_column) sc.id_column = config.id_column;
        if (config.qty_column) sc.qty = config.qty_column;
        if (config.qty_unit_column) sc.qty_unit = config.qty_unit_column;
        if (config.request_no_column) sc.request_no = config.request_no_column;
        if (config.request_pos_column) sc.req_pos = config.request_pos_column;
        if (config.territory_column) sc.territory_column = config.territory_column;
        if (config.territory_value) sc.territory_value = config.territory_value;
        unit.specialColumns = sc;
        if (config.exclude_columns) {
            unit.excludeColumns = [...config.exclude_columns];
        }
        if (config.csv_params) {
            unit.csvParams = { ...(unit.csvParams || {}), ...config.csv_params };
        }
    }

    function applyColumnSuggestions(unit: any, suggestions: Record<string, string>): void {
        const cols = new Set(unit.columns || []);
        const sc = unit.specialColumns || {};
        const titleCols: string[] = [];
        const excludeCols: string[] = [];
        unit.suggestedColumns = new Set<string>();
        Object.entries(suggestions).forEach(([col, param]) => {
            if (!cols.has(col)) return;
            unit.suggestedColumns.add(col);
            if (param === 'title') titleCols.push(col);
            else if (param === 'exclude') excludeCols.push(col);
            else if (param === 'id_column') sc.id_column = col;
            else if (param === 'qty') sc.qty = col;
            else if (param === 'qty_unit') sc.qty_unit = col;
            else if (param === 'request_no') sc.request_no = col;
            else if (param === 'request_pos') sc.req_pos = col;
            else if (param === 'territory_column') sc.territory_column = col;
        });
        unit.specialColumns = sc;
        if (titleCols.length) unit.titleColumns = titleCols;
        if (excludeCols.length) unit.excludeColumns = excludeCols;
    }

    function isSuggestedColumn(unit: any, col: string): boolean {
        return !!(unit.suggestedColumns && unit.suggestedColumns.has(col));
    }

    function colOptionLabel(unit: any, col: string): string {
        return isSuggestedColumn(unit, col) ? `${col} 🧠` : col;
    }

    function renderMassPreview(idx: number, containerId?: string): void {
        const fileObj = state.loadedFiles[idx];
        const preview = fileObj.preview || [];
        const container = containerId ? getEl<HTMLElement>(containerId) : getEl<HTMLElement>(`preview_${idx}`);
        if (!container || !preview.length) return;
        const hdrInputId = containerId ? containerId.replace('preview_', 'hdr_') : `hdr_${idx}`;
        const hdrRow = parseInt(getInput(hdrInputId)?.value || String(fileObj.headerRow || 0));
        let html = '<div style="overflow-x:auto; max-height:200px; margin-top:8px; border:1px solid #ddd; border-radius:4px;"><table style="font-size:11px; width:100%; border-collapse:collapse;">';
        preview.forEach((row, ridx) => {
            const isHeader = ridx === hdrRow;
            const bg = isHeader ? 'background:#d1ecf1; font-weight:bold;' : (ridx < hdrRow ? 'background:#f8f9fa; color:#666;' : '');
            html += `<tr style="${bg}"><td style="padding:2px 6px; border:1px solid #eee; white-space:nowrap; color:#888;">${ridx}</td>`;
            row.forEach(cell => {
                const display = String(cell).substring(0, 60);
                html += `<td style="padding:2px 6px; border:1px solid #eee; white-space:nowrap; max-width:200px; overflow:hidden; text-overflow:ellipsis;">${display}</td>`;
            });
            html += '</tr>';
        });
        html += '</table></div>';
        container.innerHTML = html;
    }

    function groupFilesByColumns(): any[] {
        const groups: Record<string, any> = {};
        state.loadedFiles.forEach((f, idx) => {
            const key = (f.columns || []).join('|');
            if (!groups[key]) {
                groups[key] = { columns: f.columns || [], files: [], indices: [] };
            }
            groups[key].files.push(f);
            groups[key].indices.push(idx);
        });
        return Object.values(groups);
    }

    // ---------- Dual-list helpers ----------
    (window as any).dualListMove = function (availId: string, selId: string, action: string) {
        const avail = getSelect(availId);
        const sel = getSelect(selId);
        if (!avail || !sel) return;
        if (action === 'add') {
            Array.from(avail.selectedOptions).forEach(opt => {
                opt.selected = false;
                sel.appendChild(opt);
            });
        } else if (action === 'remove') {
            Array.from(sel.selectedOptions).forEach(opt => {
                opt.selected = false;
                avail.appendChild(opt);
            });
        } else if (action === 'up') {
            Array.from(sel.selectedOptions).forEach(opt => {
                if (opt.previousElementSibling) sel.insertBefore(opt, opt.previousElementSibling);
            });
        } else if (action === 'down') {
            Array.from(sel.selectedOptions).reverse().forEach(opt => {
                if (opt.nextElementSibling) sel.insertBefore(opt.nextElementSibling.nextElementSibling, opt);
            });
        }
    };

    (window as any).dualListGetValues = function (selId: string): string[] {
        const sel = getSelect(selId);
        return sel ? Array.from(sel.options).map((o: HTMLOptionElement) => o.value) : [];
    };

    (window as any).dualListInit = function (availId: string, selId: string, allItems: string[], selectedItems: string[]) {
        const avail = getSelect(availId);
        const sel = getSelect(selId);
        if (!avail || !sel) return;
        const selSet = new Set(selectedItems);
        avail.innerHTML = allItems.filter(c => !selSet.has(c)).map(c => `<option value="${c}">${c}</option>`).join('');
        sel.innerHTML = selectedItems.map(c => `<option value="${c}">${c}</option>`).join('');
    };

    getButton('configure-cols-btn')!.onclick = async () => {
        if (state.loadedFiles.length === 0) return;
        // Toggle: если панель уже открыта — закрываем
        if (getButton('apply-cols')) {
            getEl<HTMLElement>('mass-classify-status')!.innerHTML = '';
            return;
        }
        // Автоопределяем колонки для ВСЕХ единиц
        for (let idx = 0; idx < state.loadedFiles.length; idx++) {
            const f = state.loadedFiles[idx];
            await autoDetectColumns(f, idx);
        }

        // Загружаем уникальные территории из активных справочников
        let terrValues: string[] = [];
        try {
            const terrRes = await apiCall('/api/territory_values', 'GET');
            terrValues = terrRes?.territory_values || [];
        } catch (e) {
            console.error('Failed to load territory values:', e);
        }
        const hasTerritory = terrValues.length > 0;

        const fileGroups = groupFilesByColumns();
        let html = '<div class="card"><h3>Настройка столбцов файлов</h3>';
        html += '<p style="font-size:13px; color:#666;">Файлы сгруппированы по одинаковой структуре колонок. Выберите строку заголовков, колонки для формирования наименования и разделитель. Строка заголовков подсвечена голубым.</p>';

        fileGroups.forEach((group, gidx) => {
            const f = group.files[0];
            const sc = f.specialColumns || {};
            const isCsv = group.files.some((fi: any) => {
                const n = fi.name.toLowerCase();
                return n.endsWith('.csv') || n.endsWith('.txt');
            });
            const isDocx = group.files.some((fi: any) => fi.name.toLowerCase().endsWith('.docx'));
            const csvEnc = f.csvParams?.encoding || 'utf-8';
            const csvDelim = f.csvParams?.delimiter || ',';
            const showCols = !f.docxStandard;
            const fileNames = group.files.map((fi: any) => fi.name).join(', ');
            const titleCols = f.titleColumns || [];
            const titleDelim = f.titleDelimiter || ' ';
            const hasExcel = group.files.some((fi: any) => ['.xlsx', '.xls', '.xlsb', '.ods'].includes(fi.ext));

            html += `<div style="margin-bottom:16px; padding:12px; border:1px solid #e0e0e0; border-radius:8px;">
                <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
                    <div><b>Группа ${gidx + 1}</b> <span style="font-size:12px; color:#666;">(${group.files.length} файл(ов): ${fileNames})</span></div>
                    <button data-action="autoDetectGroupColumns" data-gidx="${gidx}" class="secondary" style="padding:4px 12px; font-size:12px;">🔄 Автоопределить</button>
                </div>`;

            if (isDocx) {
                html += `<div id="docx_info_${gidx}" style="margin-bottom:8px; font-size:13px;">
                    ${f.docxStandard ? '<span style="color:green;">✅ Обнаружена стандартная спецификация. Колонки определены автоматически.</span>' : ''}
                </div>`;
            }

            if (isCsv) {
                html += `<div style="display:grid; grid-template-columns: 1fr 1fr; gap:8px; font-size:13px; margin-bottom:8px;">
                    <div><label>Кодировка</label>
                        <select id="enc_${gidx}">
                            <option value="utf-8" ${csvEnc === 'utf-8' ? 'selected' : ''}>UTF-8</option>
                            <option value="utf-8-sig" ${csvEnc === 'utf-8-sig' ? 'selected' : ''}>UTF-8-BOM</option>
                            <option value="cp1251" ${csvEnc === 'cp1251' ? 'selected' : ''}>Windows-1251</option>
                            <option value="cp866" ${csvEnc === 'cp866' ? 'selected' : ''}>CP866</option>
                            <option value="utf-16" ${csvEnc === 'utf-16' ? 'selected' : ''}>UTF-16</option>
                        </select></div>
                    <div><label>Разделитель CSV</label>
                        <select id="delim_${gidx}">
                            <option value="," ${csvDelim === ',' ? 'selected' : ''}>Запятая (,)</option>
                            <option value=";" ${csvDelim === ';' ? 'selected' : ''}>Точка с запятой (;)</option>
                            <option value="\t" ${csvDelim === '\t' ? 'selected' : ''}>Табуляция (\t)</option>
                            <option value="|" ${csvDelim === '|' ? 'selected' : ''}>Вертикальная черта (|)</option>
                        </select></div>
                </div>`;
            }

            html += `<div style="display:grid; grid-template-columns: 1fr 1fr; gap:8px; font-size:13px;">
                <div><label>Строка заголовков (0 = первая)</label>
                    <input type="number" id="hdr_${gidx}" value="${f.headerRow || 0}" min="0" max="20" style="width:80px;" data-action="renderMassGroupPreview" data-gidx="${gidx}"></div>
                <div style="grid-column: 1 / -1;">
                    <label>Наименование (основные колонки для склейки)</label>
                    <div style="display:flex; gap:6px; align-items:stretch; margin-top:4px;">
                        <div style="flex:1; display:flex; flex-direction:column;">
                            <span style="font-size:11px; color:#555;">Доступные</span>
                            <select id="title_avail_${gidx}" size="4" style="width:100%; flex:1;" ${!showCols ? 'disabled' : ''}></select>
                        </div>
                        <div style="display:flex; flex-direction:column; justify-content:center; gap:4px;">
                            <button type="button" data-action="dualListMove" data-args="title_avail_${gidx},title_sel_${gidx},add">→</button>
                            <button type="button" data-action="dualListMove" data-args="title_avail_${gidx},title_sel_${gidx},remove">←</button>
                            <button type="button" data-action="dualListMove" data-args="title_avail_${gidx},title_sel_${gidx},up">↑</button>
                            <button type="button" data-action="dualListMove" data-args="title_avail_${gidx},title_sel_${gidx},down">↓</button>
                        </div>
                        <div style="flex:1; display:flex; flex-direction:column;">
                            <span style="font-size:11px; color:#555;">Выбранные (порядок склейки)</span>
                            <select id="title_sel_${gidx}" size="4" style="width:100%; flex:1;" ${!showCols ? 'disabled' : ''}></select>
                        </div>
                    </div>
                </div>
                <div>
                    <label>Разделитель для склейки</label>
                    <input type="text" id="title_delim_${gidx}" value="${titleDelim}" style="width:120px;" placeholder="пробел">
                    <div style="font-size:11px; color:#888;">Например: пробел, ", ", ";"</div>
                </div>
                <div><label>Количество</label>
                    <select id="qty_${gidx}" ${!showCols ? 'disabled' : ''}><option value="">-- нет --</option>${f.columns.map((c: string) => `<option value="${c}" ${sc.qty === c ? 'selected' : ''}>${colOptionLabel(f, c)}</option>`).join('')}</select></div>
                <div><label>Ед. изм. (км→м)</label>
                    <select id="qtyunit_${gidx}" ${!showCols ? 'disabled' : ''}><option value="">-- нет --</option>${f.columns.map((c: string) => `<option value="${c}" ${sc.qty_unit === c ? 'selected' : ''}>${colOptionLabel(f, c)}</option>`).join('')}</select></div>
                <div><label>Номер заявки</label>
                    <select id="reqno_${gidx}" ${!showCols ? 'disabled' : ''}><option value="">-- нет --</option>${hasExcel ? `<option value="__sheet_name__" ${sc.request_no === '__sheet_name__' ? 'selected' : ''}>Имя листа</option>` : ''}${f.columns.map((c: string) => `<option value="${c}" ${sc.request_no === c ? 'selected' : ''}>${colOptionLabel(f, c)}</option>`).join('')}</select></div>
                <div><label>Позиция в заявке</label>
                    <select id="reqpos_${gidx}" ${!showCols ? 'disabled' : ''}><option value="">-- нет --</option>${f.columns.map((c: string) => `<option value="${c}" ${sc.req_pos === c ? 'selected' : ''}>${colOptionLabel(f, c)}</option>`).join('')}</select></div>
                <div><label>Столбец ID (req_id)</label>
                    <select id="idcol_${gidx}"><option value="">-- нет --</option>${f.columns.map((c: string) => `<option value="${c}" ${sc.id_column === c ? 'selected' : ''}>${colOptionLabel(f, c)}</option>`).join('')}</select></div>
                ${hasTerritory ? `<div><label>Территория (столбец файла)</label>
                    <select id="terrcol_${gidx}" ${!showCols ? 'disabled' : ''} data-action="clearTerritoryPair" data-gidx="${gidx}" data-target="terrval_${gidx}"><option value="">-- нет --</option>${f.columns.map((c: string) => `<option value="${c}" ${sc.territory_column === c ? 'selected' : ''}>${colOptionLabel(f, c)}</option>`).join('')}</select></div>
                <div><label>Территория (из справочника)</label>
                    <select id="terrval_${gidx}" ${!showCols ? 'disabled' : ''} data-action="clearTerritoryPair" data-gidx="${gidx}" data-target="terrcol_${gidx}"><option value="">-- нет --</option>${terrValues.map(tv => `<option value="${tv}" ${tv === sc.territory_value ? 'selected' : ''}>${tv}</option>`).join('')}</select></div>` : ''}
                <div style="grid-column: 1 / -1;">
                    <label>Исключить колонки (не обрабатывать)</label>
                    <select id="exclude_${gidx}" multiple size="2" style="width:100%;" ${!showCols ? 'disabled' : ''}>
                        ${f.columns.map((c: string) => `<option value="${c}">${colOptionLabel(f, c)}</option>`).join('')}
                    </select>
                    <div style="font-size:11px; color:#888;">Зажмите Ctrl/Cmd для выбора нескольких</div>
                </div>
            </div>
            <div id="preview_${gidx}"></div>
        </div>`;
        });
        html += '<button id="apply-cols">Применить</button></div>';
        getEl<HTMLElement>('mass-classify-status')!.innerHTML = html;

        // Инициализируем dual-list выборы столбцов наименования
        fileGroups.forEach((group, gidx) => {
            const f = group.files[0];
            const titleCols = f.titleColumns || [];
            const allCols = f.columns || [];
            dualListInit(`title_avail_${gidx}`, `title_sel_${gidx}`, allCols, titleCols);
            [`title_avail_${gidx}`, `title_sel_${gidx}`].forEach(selId => {
                const sel = getSelect(selId);
                if (!sel) return;
                Array.from(sel.options).forEach(opt => {
                    if (isSuggestedColumn(f, opt.value)) {
                        opt.textContent = `${opt.value} 🧠`;
                    }
                });
            });
        });

        // Определяем функции ДО их использования
        (window as any).autoDetectGroupColumns = async (gidx: number) => {
            const group = fileGroups[gidx];
            for (const idx of group.indices) {
                await autoDetectColumns(state.loadedFiles[idx], idx);
            }
            // Refresh UI
            (getButton('configure-cols-btn')!.onclick as any)();
        };

        (window as any).renderMassGroupPreview = (gidx: number) => {
            const group = fileGroups[gidx];
            const firstIdx = group.indices[0];
            renderMassPreview(firstIdx, `preview_${gidx}`);
        };

        // Отрисовываем preview для первого файла каждой группы
        fileGroups.forEach((group, gidx) => {
            (window as any).renderMassGroupPreview(gidx);
        });

        getButton('apply-cols')!.onclick = async () => {
            for (const group of fileGroups) {
                const gidx = fileGroups.indexOf(group);
                const hdrInput = getInput(`hdr_${gidx}`);
                const delimInput = getInput(`title_delim_${gidx}`);
                const titleCols = dualListGetValues(`title_sel_${gidx}`);
                const hdrRow = hdrInput ? parseInt(hdrInput.value) || 0 : 0;
                const titleDelim = delimInput ? delimInput.value : ' ';
                const qtyVal = getSelect(`qty_${gidx}`)?.value || '';
                const qtyUnitVal = getSelect(`qtyunit_${gidx}`)?.value || '';
                const reqnoVal = getSelect(`reqno_${gidx}`)?.value || '';
                const reqposVal = getSelect(`reqpos_${gidx}`)?.value || '';
                const excludeSel = getSelect(`exclude_${gidx}`);
                const excludeCols = excludeSel && !excludeSel.disabled
                    ? Array.from(excludeSel.selectedOptions).map(o => o.value)
                    : [];
                const encSel = getSelect(`enc_${gidx}`);
                const delimSel = getSelect(`delim_${gidx}`);

                const terrColVal = getSelect(`terrcol_${gidx}`)?.value || '';
                const terrValVal = getSelect(`terrval_${gidx}`)?.value || '';
                group.files.forEach((f: any) => {
                    f.titleColumns = titleCols;
                    f.titleDelimiter = titleDelim;
                    f.headerRow = hdrRow;
                    const idColVal = getSelect(`idcol_${gidx}`)?.value || '';
                    f.specialColumns = { qty: qtyVal, qty_unit: qtyUnitVal, request_no: reqnoVal, req_pos: reqposVal, id_column: idColVal, territory_column: terrColVal, territory_value: terrValVal };
                    f.excludeColumns = excludeCols;
                    f.suggestedColumns = new Set<string>();
                    if (encSel || delimSel) {
                        f.csvParams = {
                            encoding: encSel ? encSel.value : (f.csvParams?.encoding || 'utf-8'),
                            delimiter: delimSel ? delimSel.value : (f.csvParams?.delimiter || ',')
                        };
                    }
                });

                // Persist the chosen configuration as a user preset
                const sampleUnit = group.files[0];
                const headers = sampleUnit.columns || [];
                if (headers.length) {
                    try {
                        await apiCall('/api/column_presets/save', 'POST', {
                            source_type: 'req',
                            headers,
                            column_config: buildColumnConfig(sampleUnit),
                        });
                    } catch (e) {
                        console.error('Failed to save column preset:', e);
                    }
                }
            }
            showToast('Настройки столбцов сохранены', 'success');
            getEl<HTMLElement>('mass-classify-status')!.innerHTML = '';
        };
    };

    getButton('classify-mass-btn')!.onclick = async () => {
        if (!state.loadedFiles.length) return;
        const formData = new FormData();
        const unitNames: string[] = [];
        const unitSheetNames: string[] = [];
        const colMap: Record<string, any> = {};
        const csvParams: Record<string, any> = {};
        const excludeMap: Record<string, any> = {};
        const delimMap: Record<string, any> = {};
        const idColumnsMap: Record<string, any> = {};
        const territoryColumnsMap: Record<string, any> = {};
        const territoryValuesMap: Record<string, any> = {};
        const qtyColumnsMap: Record<string, any> = {};
        const qtyUnitColumnsMap: Record<string, any> = {};
        const requestNoColumnsMap: Record<string, any> = {};
        const requestPosColumnsMap: Record<string, any> = {};
        for (const unit of state.loadedFiles) {
            formData.append('files', unit.file);
            unitNames.push(unit.name);
            unitSheetNames.push(unit.sheetName || '');
            colMap[unit.name] = unit.titleColumns;
            delimMap[unit.name] = unit.titleDelimiter || ' ';
            excludeMap[unit.name] = unit.excludeColumns || [];
            if (unit.csvParams && (unit.csvParams.encoding || unit.csvParams.delimiter)) {
                csvParams[unit.name] = unit.csvParams;
            }
            if (unit.specialColumns?.id_column) {
                idColumnsMap[unit.name] = unit.specialColumns.id_column;
            }
            if (unit.specialColumns?.territory_column) {
                territoryColumnsMap[unit.name] = unit.specialColumns.territory_column;
            }
            if (unit.specialColumns?.territory_value) {
                territoryValuesMap[unit.name] = unit.specialColumns.territory_value;
            }
            if (unit.specialColumns?.qty) {
                qtyColumnsMap[unit.name] = unit.specialColumns.qty;
            }
            if (unit.specialColumns?.qty_unit) {
                qtyUnitColumnsMap[unit.name] = unit.specialColumns.qty_unit;
            }
            if (unit.specialColumns?.request_no) {
                requestNoColumnsMap[unit.name] = unit.specialColumns.request_no;
            }
            if (unit.specialColumns?.req_pos) {
                requestPosColumnsMap[unit.name] = unit.specialColumns.req_pos;
            }
        }
        formData.append('unit_names', JSON.stringify(unitNames));
        formData.append('unit_sheet_names', JSON.stringify(unitSheetNames));
        formData.append('title_columns', JSON.stringify(colMap));
        formData.append('title_delimiters', JSON.stringify(delimMap));
        formData.append('exclude_columns', JSON.stringify(excludeMap));
        formData.append('csv_params', JSON.stringify(csvParams));
        formData.append('id_columns', JSON.stringify(idColumnsMap));
        formData.append('territory_columns', JSON.stringify(territoryColumnsMap));
        formData.append('territory_values', JSON.stringify(territoryValuesMap));
        formData.append('qty_columns', JSON.stringify(qtyColumnsMap));
        formData.append('qty_unit_columns', JSON.stringify(qtyUnitColumnsMap));
        formData.append('request_no_columns', JSON.stringify(requestNoColumnsMap));
        formData.append('request_pos_columns', JSON.stringify(requestPosColumnsMap));

        // spec_config keyed by unit name
        const specCfg: Record<string, any> = {};
        for (const unit of state.loadedFiles) {
            const pf = unit.sourceFile;
            if (pf && (pf.specMode === 'auto' || pf.specMode === 'forced')) {
                specCfg[unit.name] = {
                    enabled: true,
                    columns: unit.specColumns || pf.specColumns || {},
                    delimiters: unit.specDelimiters || pf.specDelimiters || {}
                };
            }
        }
        formData.append('spec_config', JSON.stringify(specCfg));

        const result = await apiCall('/api/classify_mass', 'POST', formData, true);
        if (result) {
            // Keep only rows with valid subclass
            state.massSessionId = result.session_id || null;
            state.classifiedGroups = {};
            Object.entries(result.groups).forEach(([groupKey, rows]: [string, any]) => {
                const validRows = (rows || []).filter(r => r.subclass != null && String(r.subclass).trim() !== '' && String(r.subclass).trim().toLowerCase() !== 'nan');
                if (validRows.length) {
                    state.classifiedGroups[groupKey] = validRows;
                }
            });
            state.groupList = Object.keys(state.classifiedGroups);
            state.classifiedStats = {};
            
            let statsHtml = '<div class="success" style="margin-top:12px;">';
            state.groupList.forEach(groupKey => {
                const validRows = state.classifiedGroups[groupKey] || [];
                state.classifiedStats[groupKey] = validRows.length;
                if (!validRows.length) return;
                
                const groupLabel = state.refGroupsMeta.find(g => g.value === groupKey)?.label || groupKey;
                statsHtml += `<div style="margin-top:8px;"><strong>Группа: ${groupLabel}</strong> — ${validRows.length} строк</div>`;
                
                const classCounts: Record<string, number> = {};
                validRows.forEach(r => {
                    const cls = r.class || 'Не указан';
                    classCounts[cls] = (classCounts[cls] || 0) + 1;
                });
                
                Object.entries(classCounts).forEach(([cls, count]) => {
                    statsHtml += `<div style="padding-left:16px; font-size:0.95em;">Класс: ${cls} — ${count} строк</div>`;
                });
            });
            statsHtml += '</div>';
            getEl<HTMLElement>('mass-groups-info')!.innerHTML = statsHtml;
            
            getEl<HTMLElement>('mass-edit-toggle-btn')!.style.display = 'inline-block';
            autoSelectMassGroups();
        }
    };

    getEl<HTMLElement>('mass-edit-toggle-btn')!.onclick = () => {
        if (!state.massSessionId) {
            showToast('Нет session_id для редактора', 'error');
            return;
        }
        state.tableEditor.open({ source: 'req', sessionId: state.massSessionId, title: 'Редактор загруженных данных' });
    };

    function sanitizeResults(res: any) {
        if (!res) return res;
        if (res.search_mode === 'optimal' && Array.isArray(res.data)) {
            res.data.forEach(row => {
                if (!row.ref_id && row.ref_id !== 0 && row.ref_id !== '0') {
                    row.analog_counts = 0;
                }
            });
        } else if (res.search_mode === 'full' && Array.isArray(res.groups)) {
            res.groups.forEach(g => {
                const validItems = (g.items || []).filter(item => item.ref_id || item.ref_id === 0 || item.ref_id === '0');
                if (!validItems.length) {
                    g.analog_counts = 0;
                    g.items = [];
                } else {
                    g.items = validItems;
                    g.analog_counts = validItems.length;
                }
            });
        }
        return res;
    }

    function updateSearchStats(res: any) {
        const container = getEl<HTMLElement>('mass-search-stats');
        if (!container || !res || !res.stats) return;
        container.innerHTML = '';

        let html = '<div class="info" style="margin-bottom:12px;"><div><strong>Статистика по результатам поиска:</strong></div>';
        const stats = res.stats;
        Object.entries(stats).forEach(([grp, s]: [string, any]) => {
            const groupLabel = state.refGroupsMeta.find(g => g.value === grp)?.label || grp;
            html += `<div>Для группы "${groupLabel}" аналоги подобраны для ${s.with_analogs} позиций, без аналогов - ${s.without_analogs} позиций, закрыто ${s.pct} % потребности (всего загружено ${s.total_loaded})</div>`;
        });
        html += '</div>';
        container.innerHTML = html;
    }

    getButton('show-no-analogs-btn')!.onclick = async () => {
        if (!state.massFileId) return;
        const container = getEl<HTMLElement>('mass-no-analogs-result')!;
        const btn = getButton('show-no-analogs-btn')!;
        // Если уже отображается — скрываем
        if (container.innerHTML.trim() !== '') {
            container.innerHTML = '';
            btn.innerText = 'Показать позиции без аналогов';
            return;
        }
        // Загружаем и показываем
        showLoader('Загрузка позиций без аналогов...');
        const res = await apiCall(`/api/mass_search_no_analogs/${state.massFileId}`, 'GET');
        hideLoader();
        if (!res || !res.no_analogs || !res.no_analogs.length) {
            container.innerHTML = '<div class="success">Все позиции имеют аналоги!</div>';
            btn.innerText = 'Скрыть позиции без аналогов';
            return;
        }
        const noAnalogs = res.no_analogs;
        if (state.searchMode === 'optimal') {
            await getMassColumns();
            let cols = filterMassColumns(Object.keys(noAnalogs[0] || {}));
            if (!cols.length) cols = Object.keys(noAnalogs[0] || {}).filter((c: string) => c !== 'srch_key');
            createVirtualTable('mass-no-analogs-result', noAnalogs, cols, { editable: false, pagination: false, virtualDom: false });
        } else {
            let html = '<div class="card-title" style="font-size:1.1rem; margin-bottom:8px;">Позиции без аналогов</div>';
            noAnalogs.forEach(g => {
                html += `
                <div class="req-card">
                    <div class="req-header">
                        <div class="req-id">${g.req_id}</div>
                        <div class="req-text">${g.title_full_req || ''}</div>
                        <div class="req-status" style="color:#dc3545;">Нет аналогов</div>
                    </div>
                </div>`;
            });
            container.innerHTML = html;
        }
        btn.innerText = 'Скрыть позиции без аналогов';
    };

    function renderOptimalResults(data: any[], columns: string[]) {
        const container = getEl<HTMLElement>('mass-search-result')!;
        if (!data || !data.length) {
            container.innerHTML = '<div class="warning">Нет данных</div>';
            return;
        }
        const filteredData = data.filter(row => row.ref_id || row.ref_id === 0 || row.ref_id === '0');
        if (!filteredData.length) {
            container.innerHTML = '<div class="warning">Нет позиций с аналогами</div>';
            return;
        }
        const displayCols = columns.filter(c => c !== 'srch_key');
        container.innerHTML = '<div class="mass-search-result-wrapper"><div id="mass-search-result-table"></div></div>';
        createVirtualTable('mass-search-result-table', filteredData, displayCols, {
            editable: false,
            pagination: false,
            virtualDom: false,
            layout: "fitDataTable",
            rowFormatter: function (row: any) {
                const data = row.getData();
                if (data.qty_shortage === true || data.qty_shortage === 'true') {
                    row.getElement().style.backgroundColor = '#fff3cd';
                }
            }
        });
    }

    function renderFullResults(groups: any[], columns: string[]) {
        const container = getEl<HTMLElement>('mass-search-result')!;
        console.log('renderFullResults', groups?.length, columns);
        if (!groups || !groups.length) {
            container.innerHTML = '<div class="warning">Нет данных</div>';
            return;
        }
        const filteredGroups = groups.filter(g => g.analog_counts > 0 && g.items && g.items.length > 0);
        if (!filteredGroups.length) {
            container.innerHTML = '<div class="warning">Нет позиций с аналогами</div>';
            return;
        }
        // Определяем какие колонки ref показывать (исключая служебные)
        const skipCols = ['req_id','title_full_req','title_req_norm','srch_key','analog_counts','strategy','group','group_prod','ref_id'];
        let refCols = columns.filter(c => !skipCols.includes(c));
        // Fallback: если колонок для вывода нет, показываем title_full_ref и strategy
        if (!refCols.length) {
            refCols = ['title_full_ref'];
            if (columns.includes('strategy')) refCols.push('strategy');
        }
        let html = '';
        filteredGroups.forEach(g => {
            const count = g.analog_counts || g.items.length;
            const reqNorm = g.title_req_norm ? `<div style="font-size:12px; color:#666; margin-top:4px;">${g.title_req_norm}</div>` : '';
            html += `
            <div class="req-card">
                <div class="req-header" data-action="toggleReqDetails">
                    <div class="req-id">${g.req_id}</div>
                    <div class="req-text">${g.title_full_req}${reqNorm}</div>
                    <div class="req-status">Позиции сопоставлены</div>
                    <div class="req-count">${count}</div>
                    <button class="req-toggle" data-action="toggleReqDetails">
                        <i class="fas fa-chevron-down"></i>
                    </button>
                </div>
                <div class="req-details">
                    <table class="ref-table">
                        <thead><tr><th>id Запасы</th>`;
            refCols.forEach(c => { html += `<th>${getDisplayColumnName(c)}</th>`; });
            html += `</tr></thead><tbody>`;
            g.items.forEach((item: any) => {
                const shortageStyle = (item.qty_shortage === true || item.qty_shortage === 'true') ? ' style="background:#fff3cd"' : '';
                html += `<tr${shortageStyle}><td>${item.ref_id !== undefined && item.ref_id !== null ? item.ref_id : ''}</td>`;
                refCols.forEach(c => {
                    const val = item[c] !== undefined && item[c] !== null ? formatBoolean(item[c]) : '';
                    html += `<td>${val}</td>`;
                });
                html += '</tr>';
            });
            html += `</tbody></table></div></div>`;
        });
        container.innerHTML = html;
    }

    (window as any).toggleReqDetails = function (header: HTMLElement) {
        const details = header.parentElement.querySelector('.req-details');
        const icon = header.querySelector('.req-toggle i');
        if (details.classList.contains('open')) {
            details.classList.remove('open');
            icon.classList.remove('fa-chevron-up');
            icon.classList.add('fa-chevron-down');
        } else {
            details.classList.add('open');
            icon.classList.remove('fa-chevron-down');
            icon.classList.add('fa-chevron-up');
        }
    };

    async function renderResults(res: any): Promise<void> {
        sanitizeResults(res);
        state.massResultData = res;
        currentPage = res.page;
        totalPages = res.total_pages;
        currentColumns = res.columns;
        await getMassColumns();
        let displayCols = filterMassColumns(res.columns);
        if (!displayCols.length) {
            displayCols = (res.columns || []).filter((c: string) => c !== 'srch_key');
        }
        if (res.search_mode === 'optimal') {
            renderOptimalResults(res.data, displayCols);
        } else {
            renderFullResults(res.groups, displayCols);
        }
        updateSearchStats(res);
        getEl<HTMLElement>('show-no-analogs-btn')!.style.display = 'inline-block';
        getEl<HTMLElement>('clear-mass-results-btn')!.style.display = 'inline-block';
        getEl<HTMLElement>('mass-no-analogs-result')!.innerHTML = '';
        const paginationDiv = getEl<HTMLElement>('pagination-controls')!;
        if (totalPages > 1) {
            paginationDiv.style.display = 'flex';
            const label = res.search_mode === 'optimal' ? 'строк' : 'позиций';
            getEl<HTMLElement>('pagination-info')!.innerText = `Страница ${currentPage} из ${totalPages}`;
            getButton('prev-page')!.disabled = (currentPage <= 1);
            getButton('next-page')!.disabled = (currentPage >= totalPages);
        } else {
            paginationDiv.style.display = 'none';
        }
        getEl<HTMLElement>('download-mass-menu-btn')!.style.display = 'inline-block';
        const totalLabel = res.search_mode === 'optimal' ? 'строк' : 'позиций';
        showToast(`Найдено ${res.total_rows} ${totalLabel}. Показана страница ${currentPage}.`, 'success');
    }

    async function loadSearchPage(page: number): Promise<void> {
        if (!state.massFileId) return;
        const searchMode = state.searchMode || 'optimal';
        const perPage = state.massPerPage || 100;
        const res = await apiCall(`/api/mass_search_page/${state.massFileId}?page=${page}&per_page=${perPage}&search_mode=${searchMode}`, 'GET');
        console.log('loadSearchPage res', res);
        if (res && (res.data !== undefined || res.groups !== undefined)) {
            await renderResults(res);
        } else {
            showToast('Нет результатов', 'warning');
        }
    }

    document.querySelectorAll<HTMLElement>('.mode-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll<HTMLElement>('.mode-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            state.searchMode = (btn.dataset.mode as 'optimal' | 'full') || 'optimal';
        });
    });

    getButton('run-mass-search')!.onclick = async () => {
        const selected = getSelectedMassGroups();
        if(!selected.length) return;
        const strategyMode = getSelect('mass-strategy-mode')!.value;
        const payload: Record<string, any> = {
            groups: selected,
            requests: state.classifiedGroups,
            strategies: state.strategies,
            search_mode: state.searchMode,
            strategy_mode: strategyMode,
            page: 1,
            per_page: state.massPerPage
        };
        const res = await apiCall('/api/mass_search', 'POST', payload);
        console.log('mass_search initial res', res);
        if (res && res.file_id) {
            state.massFileId = res.file_id;
            await renderResults(res);
        } else {
            showToast('Нет результатов', 'warning');
        }
    };

    getButton('prev-page')!.onclick = () => {
        if (currentPage > 1) loadSearchPage(currentPage - 1);
    };
    getButton('next-page')!.onclick = () => {
        if (currentPage < totalPages) loadSearchPage(currentPage + 1);
    };

    // ---------- Mass search column settings ----------
    getInput('mass-per-page')!.value = String(state.massPerPage);
    getInput('mass-per-page')!.onchange = (e: Event) => {
        const val = parseInt((e.target as HTMLInputElement).value);
        state.massPerPage = val;
        localStorage.setItem('massPerPage', String(val));
        if (state.massFileId) {
            loadSearchPage(1);
        }
    };

    // Кнопка "Добавить файл"
    getButton('add-mass-files-btn')!.onclick = () => {
        getInput('add-mass-files-input')!.click();
    };
    getInput('add-mass-files-input')!.onchange = async (e: Event) => {
        const files = Array.from((e.target as HTMLInputElement).files || []).filter((f: File) => {
            const n = f.name.toLowerCase();
            return n.endsWith('.csv') || n.endsWith('.xlsx') || n.endsWith('.xls') || n.endsWith('.docx') || n.endsWith('.txt');
        });
        if (files.length) {
            await processFiles(files, true);
        }
        (e.target as HTMLInputElement).value = '';
    };

    // Кнопка "Очистить результаты"
    getButton('clear-mass-results-btn')!.onclick = () => {
        clearMassResults();
    };

    function clearMassResults() {
        state.massFileId = null;
        state.massSessionId = null;
        state.classifiedGroups = {};
        state.groupList = [];
        state.classifiedStats = {};
        getEl<HTMLElement>('mass-search-result')!.innerHTML = '';
        getEl<HTMLElement>('mass-search-stats')!.innerHTML = '';
        getEl<HTMLElement>('mass-no-analogs-result')!.innerHTML = '';
        getEl<HTMLElement>('pagination-controls')!.style.display = 'none';
        getEl<HTMLElement>('download-mass-menu-btn')!.style.display = 'none';
        getEl<HTMLElement>('show-no-analogs-btn')!.style.display = 'none';
        getEl<HTMLElement>('clear-mass-results-btn')!.style.display = 'none';
    }
